import React, { useState } from 'react';
import { 
    Box, 
    Typography, 
    Paper,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    Tabs,
    Tab,
    Collapse,
    IconButton,
    Tooltip
} from '@mui/material';
import { 
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    CompareArrows as CompareIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import { CitySums, CityTerminal } from './types';
import { formatSum } from './utils';

interface EnhancedSummaryDisplayProps {
    sums: CitySums;
    citySums: CitySums;
    cityTerminals: CityTerminal[];
    processingStats?: {
        processed: number;
        matched: number;
    };
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
};

const EnhancedSummaryDisplay: React.FC<EnhancedSummaryDisplayProps> = ({
    sums,
    citySums,
    cityTerminals,
    processingStats
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [expandedCities, setExpandedCities] = useState(false);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const getDifferencePercentage = (value1: number, value2: number): number => {
        if (value2 === 0) return value1 === 0 ? 0 : 100;
        return ((value1 - value2) / value2) * 100;
    };

    const getStatusIcon = (diff: number, threshold = 0.1) => {
        if (Math.abs(diff) <= threshold) {
            return <CheckCircleIcon color="success" fontSize="small" />;
        }
        return <ErrorIcon color="error" fontSize="small" />;
    };

    const getStatusColor = (diff: number, threshold = 0.1): 'success' | 'error' | 'warning' => {
        if (Math.abs(diff) <= threshold) return 'success';
        if (Math.abs(diff) <= threshold * 10) return 'warning';
        return 'error';
    };

    const totalDifference = Math.abs(sums.total - citySums.total);
    const totalPercentDiff = getDifferencePercentage(sums.total, citySums.total);

    const cityDifferences = cityTerminals.map(terminal => {
        const pharmacyKey = terminal.created_by;
        const salesSum = sums[pharmacyKey] || 0;
        const deliverySum = citySums[pharmacyKey] || 0;
        const diff = Math.abs(salesSum - deliverySum);
        const percentDiff = getDifferencePercentage(salesSum, deliverySum);

        return {
            terminal,
            salesSum,
            deliverySum,
            diff,
            percentDiff
        };
    });

    return (
        <Box sx={{ mt: 3 }}>
            {/* Общая статистика */}
            {processingStats && (
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
                    <Typography variant="h6" gutterBottom>
                        Статистика обработки
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Chip 
                            label={`Обработано: ${processingStats.processed}`} 
                            color="info" 
                            variant="outlined"
                        />
                        <Chip 
                            label={`Совпадений: ${processingStats.matched}`} 
                            color="success" 
                            variant="outlined"
                        />
                        <LinearProgress 
                            variant="determinate" 
                            value={(processingStats.matched / processingStats.processed) * 100}
                            sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {Math.round((processingStats.matched / processingStats.processed) * 100)}%
                        </Typography>
                    </Box>
                </Paper>
            )}

            {/* Общая сводка */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CompareIcon />
                        Общая сводка
                        {getStatusIcon(totalDifference)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Выписка
                            </Typography>
                            <Typography variant="h6" color="primary">
                                {formatSum(sums.total)} ₸
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {totalPercentDiff > 0 ? 
                                <TrendingUpIcon color="error" /> : 
                                <TrendingDownIcon color="success" />
                            }
                            <Typography variant="body2" color={totalPercentDiff > 5 ? 'error' : 'success'}>
                                {Math.abs(totalPercentDiff).toFixed(1)}%
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">
                                Доставка аптеки
                            </Typography>
                            <Typography variant="h6" color="secondary">
                                {formatSum(citySums.total)} ₸
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Chip
                            icon={getStatusIcon(totalDifference)}
                            label={`Разница: ${formatSum(totalDifference)} ₸`}
                            color={getStatusColor(totalDifference)}
                            variant="filled"
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Детализация по городам */}
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6">
                            Детализация по городам
                        </Typography>
                        <Tooltip title={expandedCities ? "Свернуть" : "Развернуть"}>
                            <IconButton onClick={() => setExpandedCities(!expandedCities)}>
                                {expandedCities ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                        <Tab label="Таблица" />
                        <Tab label="Выписка" />
                        <Tab label="Доставка" />
                        <Tab label="Разница" />
                    </Tabs>

                    <TabPanel value={activeTab} index={0}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {cityDifferences.map(({ terminal, salesSum, deliverySum, diff, percentDiff }) => (
                                <Paper 
                                    key={terminal.id} 
                                    variant="outlined" 
                                    sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2">
                                            {terminal.created_by}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {terminal.address}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Код: {terminal.terminal_code}
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                                        <Typography variant="body2" color="primary">
                                            {formatSum(salesSum)} ₸
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Выписка
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ textAlign: 'right', minWidth: 120 }}>
                                        <Typography variant="body2" color="secondary">
                                            {formatSum(deliverySum)} ₸
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Доставка
                                        </Typography>
                                    </Box>
                                    
                                    <Box sx={{ textAlign: 'right', minWidth: 100 }}>
                                        <Chip
                                            size="small"
                                            icon={getStatusIcon(diff)}
                                            label={formatSum(diff) + ' ₸'}
                                            color={getStatusColor(diff)}
                                        />
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            {Math.abs(percentDiff).toFixed(1)}%
                                        </Typography>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    </TabPanel>

                    <TabPanel value={activeTab} index={1}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                Сумма операций: {formatSum(sums.total)} ₸
                            </Typography>
                            {cityTerminals.map(terminal => (
                                <Box key={terminal.id} sx={{ mb: 1 }}>
                                    <Typography sx={{ fontWeight: 'medium' }}>
                                        {terminal.created_by}: {formatSum(sums[terminal.created_by] || 0)} ₸
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {terminal.address}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </TabPanel>

                    <TabPanel value={activeTab} index={2}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                                Сумма операций: {formatSum(citySums.total)} ₸
                            </Typography>
                            {cityTerminals.map(terminal => (
                                <Box key={terminal.id} sx={{ mb: 1 }}>
                                    <Typography sx={{ fontWeight: 'medium' }}>
                                        {terminal.created_by}: {formatSum(citySums[terminal.created_by] || 0)} ₸
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {terminal.address}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </TabPanel>

                    <TabPanel value={activeTab} index={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: getStatusColor(totalDifference) === 'success' ? 'success.main' : 'error.main' }}>
                                Общая разница: {formatSum(totalDifference)} ₸
                            </Typography>
                            {cityDifferences.map(({ terminal, diff }) => (
                                <Box key={terminal.id} sx={{ mb: 1 }}>
                                    <Typography 
                                        sx={{ fontWeight: 'medium' }}
                                        color={getStatusColor(diff) === 'success' ? 'success.main' : 'error.main'}
                                    >
                                        {terminal.created_by}: {formatSum(diff)} ₸
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {terminal.address}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </TabPanel>
                </CardContent>
            </Card>
        </Box>
    );
};

export default EnhancedSummaryDisplay;
